import axios from "axios";
import { AUTH_TOKEN, AUTH_REFRESH_TOKEN } from "constants/AuthConstant";
import { API_BASE_URL } from "configs/AppConfig";

// Create a new Axios instance
const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

let failedQueue = [];
let isRefreshing = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor
service.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
service.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is 401 and it's not a retry request
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we are already refreshing the token, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return service(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN);
      if (!refreshToken) {
        // If no refresh token, logout the user
        // You might want to dispatch a logout action here
        window.location.href = "/auth/login";
        return Promise.reject(error);
      }

      try {
        // Call the refresh token endpoint
        const { data } = await axios.post(
          `${API_BASE_URL}/admin/auth/refresh-token`,
          {
            refreshToken,
          }
        );

        // Update tokens in localStorage
        localStorage.setItem(AUTH_TOKEN, data?.data?.accessToken);
        localStorage.setItem(AUTH_REFRESH_TOKEN, data?.data?.refreshToken);

        // Update the header for the current service instance and the original request
        service.defaults.headers.common["Authorization"] =
          "Bearer " + data?.data?.accessToken;
        originalRequest.headers["Authorization"] =
          "Bearer " + data?.data?.accessToken;

        // Retry all requests in the queue with the new token
        processQueue(null, data?.data?.accessToken);

        // Retry the original request
        return service(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and logout
        processQueue(refreshError, null);
        localStorage.removeItem(AUTH_TOKEN);
        localStorage.removeItem(AUTH_REFRESH_TOKEN);
        // You might want to dispatch a logout action here
        window.location.href = "/auth/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default service;
